'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Mail, Monitor } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { NoticeBanner } from '@/shared/ui';
import { SettingsCard, SettingsItem, SettingsRowGroup } from '@/shared/ui/settings';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { authService } from '@/features/auth/api/auth';
import { useAuthActions } from '@/features/auth';
import { useAuthProviders } from '@/features/auth/model/use-auth-providers';
import { startVkIdLogin } from '@/features/auth/lib/vk-id-oauth';
import {
  applySignInMethodResult,
  withSessionSwitch,
  consumeLinkError,
  consumeMergeMessage,
  markLinkFlow,
  SIGN_IN_METHODS_PATH,
} from '@/features/auth/lib/sign-in-method-link';
import { useSignInState } from '@/features/auth/model/sign-in.query';
import { getAppleOAuthUrl } from '@/features/auth/lib/apple-oauth';
import { AppleLogoIcon, GoogleLogoIcon, VkIdLogoIcon } from '@/features/auth/ui/social-icons';
import type { User } from '@/types/user';

type EmailStep = { kind: 'hidden' } | { kind: 'address' } | { kind: 'code'; email: string };

function errorText(error: unknown): string | null {
  return error instanceof ApiClientError ? error.getFirstError() : null;
}

/**
 * Sign-in methods (docs/identity/ADR-001, docs/identity/SCREENS-001): Apple, Google,
 * VK ID, e-mail by code and this browser. Adding a method that already
 * belongs to another account merges the two; the server's message is shown
 * as-is. `prefillEmail` is the checkout address in the required step after a
 * web purchase, linked with one tap.
 */
export function SignInMethodsSection({ prefillEmail }: { prefillEmail?: string | null }) {
  const t = useTranslations('auth.signInMethods');
  const tMethods = useTranslations('auth.methods');
  const queryClient = useQueryClient();
  const state = useSignInState();
  const user = queryClient.getQueryData<User>(cacheKeys.user);
  const { getGoogleOAuthUrl } = useAuthActions();
  const { isVkIdAvailable } = useAuthProviders();

  const [emailStep, setEmailStep] = useState<EmailStep>({ kind: 'hidden' });
  const [emailInput, setEmailInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Back from a Google / VK ID redirect started on this screen.
  useEffect(() => {
    setSuccess(consumeMergeMessage());
    setError(consumeLinkError());
  }, []);

  const has = (method: string) => state?.methods.includes(method) ?? false;
  const hasEmail = !!user?.email;

  const run = async (method: 'email', call: () => ReturnType<typeof authService.linkCheckoutEmail>) => {
    setBusy(true);
    setError(null);
    try {
      await withSessionSwitch(async () => applySignInMethodResult(await call(), method));
      // Full reload, as the OAuth callbacks do: after a merge the session
      // belongs to another account, and the cache wipe detaches mounted
      // queries, so this screen (and the required overlay) kept the previous
      // account's state. The merge message survives in sessionStorage and is
      // shown on mount.
      window.location.replace(SIGN_IN_METHODS_PATH);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  const sendCode = async () => {
    const email = emailInput.trim();
    setBusy(true);
    setError(null);
    try {
      await authService.sendSignInEmailCode(email);
      setEmailStep({ kind: 'code', email });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const addApple = () => {
    markLinkFlow('apple');
    window.location.href = getAppleOAuthUrl({ link: true });
  };

  const addGoogle = async () => {
    markLinkFlow('google');
    sessionStorage.setItem('oauth_redirect', SIGN_IN_METHODS_PATH);
    window.location.href = await getGoogleOAuthUrl();
  };

  const addVk = async () => {
    await startVkIdLogin({ link: true });
  };

  const trailing = (connected: boolean, onAdd: () => void) =>
    connected ? (
      <Check className="size-5 text-primary" aria-hidden />
    ) : (
      <Button variant="link" size="sm" onClick={onAdd} disabled={busy}>
        {t('add')}
      </Button>
    );

  return (
    <div className="space-y-4">
      {success && <NoticeBanner variant="success" message={success} />}
      {error && <NoticeBanner variant="error" message={error} />}

      {prefillEmail && !hasEmail && emailStep.kind === 'hidden' && (
        <div className="space-y-2">
          <Input value={prefillEmail} readOnly aria-label={t('email')} />
          <Button className="w-full" disabled={busy} onClick={() => run('email', () => authService.linkCheckoutEmail())}>
            {t('linkEmail')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setEmailStep({ kind: 'address' })}>
            {t('useAnotherEmail')}
          </Button>
        </div>
      )}

      <SettingsCard>
        <SettingsRowGroup dividerClassName="ms-[3.25rem] me-5">
          <SettingsItem
            icon={AppleLogoIcon}
            title={tMethods('apple.title')}
            showChevron={false}
            valueContent={trailing(has('apple'), addApple)}
          />
          <SettingsItem
            icon={GoogleLogoIcon}
            title={tMethods('google.title')}
            showChevron={false}
            valueContent={trailing(has('google'), addGoogle)}
          />
          {(isVkIdAvailable || has('vk')) && (
            <SettingsItem
              icon={VkIdLogoIcon}
              title={tMethods('vk.title')}
              showChevron={false}
              valueContent={trailing(has('vk'), addVk)}
            />
          )}
          <SettingsItem
            icon={Mail}
            title={t('email')}
            subtitleLines={user?.email ? [user.email] : undefined}
            showChevron={false}
            valueContent={trailing(hasEmail, () => {
              setEmailInput(prefillEmail ?? '');
              setEmailStep({ kind: 'address' });
            })}
          />
          {has('device') && (
            <SettingsItem
              icon={Monitor}
              title={t('thisDevice')}
              showChevron={false}
              valueContent={trailing(true, () => undefined)}
            />
          )}
        </SettingsRowGroup>
      </SettingsCard>

      {emailStep.kind === 'address' && (
        <div className="space-y-2">
          <Input
            type="email"
            autoComplete="email"
            placeholder={t('email')}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          <Button className="w-full" disabled={busy || !emailInput.includes('@')} onClick={sendCode}>
            {t('sendCode')}
          </Button>
        </div>
      )}

      {emailStep.kind === 'code' && (
        <div className="space-y-2">
          <p className="text-form-help text-muted-foreground">{t('codeSent', { email: emailStep.email })}</p>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('codePlaceholder')}
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
          <Button
            className="w-full"
            disabled={busy || codeInput.length !== 4}
            onClick={() => run('email', () => authService.addEmailMethod(emailStep.email, codeInput))}
          >
            {t('confirm')}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setEmailInput(emailStep.email);
              setEmailStep({ kind: 'address' });
            }}
          >
            {t('useAnotherEmail')}
          </Button>
        </div>
      )}
    </div>
  );
}
